import { Link } from "react-router-dom";
import { MktHero, MktSection } from "../components/marketing";
import { Seo } from "../lib/seo";
import { BOT_MODEL_SENTENCE, COUNTRY, LegalContact, LegalMeta, OWNER, SubprocessorList } from "./legal-common";

/**
 * Public /privacy and /terms pages.
 *
 * Counsel-reviewed and IN FORCE — no longer drafts. Every hedge that pointed at a
 * future review ("will be confirmed", "before launch") has been resolved into a
 * term a customer can actually rely on, because the service is live and people
 * are paying for it.
 *
 * The version note, the contact paragraph and the description of how a bot
 * reaches a customer's server all come from ./legal-common so these two pages and
 * the seven under /legal say the same thing in the same words. Do not re-declare
 * them locally.
 */

export function Privacy(): JSX.Element {
  return (
    <>
      <Seo
        title="Privacy Policy"
        description="How ODIN (OKMedia, Norway) is designed to handle your data, and your rights."
        path="/privacy"
      />
      <MktHero kicker="Legal" title="Privacy Policy" subhead="How ODIN is designed to handle your data." />

      <MktSection>
        <div className="mkt-prose">
          <LegalMeta />

          <h2>Data we handle</h2>
          <p>
            Account details (such as your login identity and email), the configuration you create for your
            community server (blueprints, roles, channels, moderation and integration settings), and
            operational logs needed to run the service. {BOT_MODEL_SENTENCE} On the Dedicated plan, a bot
            token you choose to provide is submitted once over an encrypted connection, stored encrypted
            server-side, and never shown back to the browser.
          </p>

          <h2>Sub-processors</h2>
          <p>
            ODIN relies on a small set of service providers to operate. The same list is published, with
            the same descriptions, on the <Link to="/legal/subprocessors">Subprocessors</Link> page:
          </p>
          <SubprocessorList />

          <h2>Retention</h2>
          <p>
            Data is retained while your account is active. When you close your account or delete a
            workspace, the associated configuration and operational data is deleted or anonymised, except
            where we are required to keep it — accounting and invoicing records, which Norwegian
            bookkeeping law obliges us to retain, and anything we must preserve to resolve an open dispute
            or meet a legal obligation. You can request deletion at any time, and we act on it as
            described under <em>Your rights</em> below.
          </p>

          <h2>Your rights</h2>
          <p>
            You can request access to, export of, or deletion of your data. Cancelling a paid plan does not
            automatically delete anything in your Discord server.
          </p>

          <h2>Contact</h2>
          <LegalContact />
        </div>
      </MktSection>
    </>
  );
}

export function Terms(): JSX.Element {
  return (
    <>
      <Seo
        title="Terms of Service"
        description="The terms for using ODIN, independently operated by OKMedia (Norway)."
        path="/terms"
      />
      <MktHero kicker="Legal" title="Terms of Service" subhead="The terms for using ODIN." />

      <MktSection>
        <div className="mkt-prose">
          <LegalMeta />

          <h2>Using ODIN</h2>
          <p>
            ODIN is a control plane for managing a single Discord community server. {BOT_MODEL_SENTENCE}{" "}
            However your server is connected, you remain responsible for how your community is run and for
            complying with Discord's own terms.
          </p>

          <h2>Availability</h2>
          <p>
            ODIN is rolling out carefully. Live deployment is locked by default and enabled deliberately.
            We make no uptime promises and provide the service on an as-available basis while it matures.
          </p>

          <h2>Plans & billing</h2>
          <p>
            Free, Core, Pro and Dedicated plans are available. Free, Core and Pro carry published,
            VAT-inclusive prices; Dedicated is quoted individually and shown as a starting price. Paid
            plans can be cancelled at any time and remain active until the end of the period you paid for.
            Refunds and cancellations are governed by our{" "}
            <Link to="/legal/refunds-and-cancellations">Refunds &amp; Cancellations</Link> policy.
          </p>

          <h2>Acceptable use</h2>
          <p>
            Don't use ODIN to break the law, abuse others, or circumvent the safety controls that hold
            changes for your approval.
          </p>

          <h2>Data & sub-processors</h2>
          <p>
            How we handle your data — including the{" "}
            <Link to="/legal/subprocessors">sub-processors</Link> we rely on — is described in the{" "}
            <Link to="/privacy">Privacy Policy</Link>. Business customers can request our{" "}
            <Link to="/legal/dpa">Data Processing Agreement</Link>.
          </p>

          <h2>Ownership</h2>
          <p>
            ODIN is independently owned and operated by {OWNER}, established in {COUNTRY}. Discord, Google,
            payment providers and other third parties are independent services that support ODIN under
            their own terms; they do not own, control or endorse ODIN.
          </p>

          <h2>Governing law</h2>
          <p>
            These Terms are governed by the laws of {COUNTRY}. Disputes that cannot be resolved between us
            are subject to the ordinary Norwegian courts. Nothing in these Terms limits mandatory consumer
            rights or protections that cannot lawfully be excluded under applicable legislation — if you
            are a consumer resident elsewhere in the EEA, you keep any right you have under your local law
            to bring a claim in the courts of your own country of residence.
          </p>

          <h2>Contact</h2>
          <LegalContact />
        </div>
      </MktSection>
    </>
  );
}
